import type { Property } from '~/core/entities/property';
import type { PropertyFilters } from '~/core/entities/property/filters';

import { useFeatureFlags } from '~/composables/useFeatureFlags';
import { defaultTwitterCardMeta } from '~/constants/seo';
import {
	PropertyTypeEnum,
	PropertyTypePlurals
} from '~/core/entities/CRMEnums';
import { AddressUtils } from '~/utils/AddressUtilsWrapper';
import { computed } from 'vue';

const getPropertyType = (
	propertyTypes: PropertyTypeEnum[],
	filteredCount: number
) => {
	if (propertyTypes.length === 1) {
		const propertyType = propertyTypes?.[0];
		return PropertyTypePlurals[propertyType!] || 'Imóveis';
	}
	return filteredCount === 1 ? 'Imóvel' : 'Imóveis';
};

const generateDescription = (
	address: string,
	propertyTypeFilters: PropertyTypeEnum[],
	filteredCount: number
): string => {
	const propertyType = getPropertyType(propertyTypeFilters, filteredCount);

	return `Mais de ${filteredCount} ${propertyType.toLowerCase()} em ${address}. Encontre opções exclusivas e a oportunidade ideal para você no PilarHomes!`;
};

const generateKeyWords = (filters: PropertyFilters): string => {
	const joinedRegions = filters.regions?.join(', ') || '';
	const joinedCities = filters.cities?.join(', ') || '';
	const joinedProperties =
		filters.propertyTypes
			?.map(property => PropertyTypePlurals[property])
			.filter(Boolean)
			.join(', ') || '';
	let keywords = '';

	if (joinedProperties) {
		keywords += joinedProperties;
	}

	if (joinedRegions) {
		if (keywords) {
			keywords += ', ';
		}
		keywords += joinedRegions;
	}

	if (joinedCities) {
		if (keywords) {
			keywords += ', ';
		}
		keywords += joinedCities;
	}

	if (keywords) {
		keywords += ', ';
	}

	keywords += 'alto padrão, À venda, PilarHomes';
	return keywords;
};

export const useSeoTagsListing = (
	filters: Ref<PropertyFilters>,
	filteredCount: Ref<number>,
	properties: Ref<Property[]>
) => {
	function getAddress(filters: Ref<PropertyFilters>): string {
		const state =
			(filters.value.states && filters.value.states.length > 0
				? filters.value.states[0]
				: AddressUtils.getDefaultState()) || 'SP';

		const region = filters.value.regions?.[0] || '';
		const city = filters.value.cities?.[0] || '';
		const macroregion = filters.value.macroregions?.[0] || '';

		const addressSlug = filters.value.addressSlug || '';

		if (addressSlug && addressSlug.includes(',')) {
			return `${addressSlug} e arredores`;
		}

		return (
			AddressUtils.formatAddressDisplay(region, city, state, macroregion) ||
			'São Paulo'
		);
	}
	const generatedTags = computed(() => {
		const propertyTypeFilters = filters.value.propertyTypes || [];
		const propertyTypeText = getPropertyType(
			propertyTypeFilters,
			filteredCount.value
		);

		const address = getAddress(filters);
		const title = `${propertyTypeText} em ${address} | PilarHomes`;
		const description = generateDescription(
			address,
			propertyTypeFilters,
			filteredCount.value
		);
		const ogDescription = description;
		const twitterDescription = description;
		const keyWords = generateKeyWords(filters.value);

		const ogImage = properties.value[0]?.images[0]?.watermarkUrl || '';

		return {
			title: title,
			description: description,
			keyWords: keyWords,
			ogTitle: title,
			ogDescription: ogDescription,
			ogImage: ogImage,
			twitterTitle: title,
			twitterDescription: twitterDescription,
			twitterImage: ogImage
		};
	});

	const updateSeoMeta = () => {
		useSeoMeta({
			title: () => generatedTags.value.title,
			description: () => generatedTags.value.description,
			keywords: () => generatedTags.value.keyWords,
			ogTitle: () => generatedTags.value.ogTitle,
			ogDescription: () => generatedTags.value.ogDescription,
			ogImage: () => generatedTags.value.ogImage,
			twitterTitle: () => generatedTags.value.twitterTitle,
			twitterDescription: () => generatedTags.value.twitterDescription,
			twitterImage: () => generatedTags.value.twitterImage,
			twitterCard: defaultTwitterCardMeta
		});
	};

	return {
		updateSeoMeta,
		pageTitle: computed(() => generatedTags.value.title),
		pageDescription: computed(() => generatedTags.value.description)
	};
};
